import {
  costAvoidance,
  expenseManagementEfficiencyGain,
  payfitPackages,
  payrollProcessEfficiencyGains,
  planningImplementationCosts,
  staffManagementEfficiencyGains,
  timeSavingsOnPerformanceManagement,
} from './forresterData';
import {
  NumberEmployeeRangeEnum,
  PayFitPackages,
  PayfitPackageEnum,
} from './types';

const getYearCostsResults = (
  payfitPackage: number,
  numberEmployee: number,
  moduleExpense: boolean,
  isFirstYear: boolean,
) => {
  const payfitImplementationCostsY1: number =
    planningImplementationCosts[payfitPackage];

  const expenseModuleCosts: number = moduleExpense
    ? (getPayFitPackage(
        payfitPackage,
        getEmployeeRangeByNbEmployees(numberEmployee, payfitPackage) || 0,
      )?.expenseModule || 0) * numberEmployee
    : 0;
  const payfitSubscriptionCosts =
    getPayFitPackage(
      payfitPackage,
      getEmployeeRangeByNbEmployees(numberEmployee, payfitPackage) || 0,
    )?.subscription || 0;
  const payfitEmployeeCosts =
    (getPayFitPackage(
      payfitPackage,
      getEmployeeRangeByNbEmployees(numberEmployee, payfitPackage) || 0,
    )?.subscriptionByPayroll || 0) * numberEmployee;
  const payfitCosts =
    expenseModuleCosts + payfitSubscriptionCosts + payfitEmployeeCosts;
  const payfitCostsYearly = payfitCosts * 12;

  return payfitCostsYearly + (isFirstYear ? payfitImplementationCostsY1 : 0);
};

export const getIndexPayfitPackageEnum = (payfitPackage: string) =>
  Object.values(PayfitPackageEnum).findIndex((x) => x === payfitPackage);

const getYearGainsResults = (
  payfitPackage: string,
  numberEmployee: number,
  moduleExpense: boolean,
) => {
  const _payfitPackage = getIndexPayfitPackageEnum(payfitPackage);

  const getPayrollProcessEfficiencyGains =
    payrollProcessEfficiencyGains[_payfitPackage] * numberEmployee;

  const getExpenseManagementEfficiencyGains = moduleExpense
    ? expenseManagementEfficiencyGain[_payfitPackage] * numberEmployee
    : 0;

  const getTimeSavingsOnPerformanceManagement =
    timeSavingsOnPerformanceManagement[_payfitPackage] * numberEmployee;

  const getCostsAvoidance = costAvoidance[_payfitPackage] * numberEmployee;

  const getStaffManagementEfficiencyGains =
    staffManagementEfficiencyGains[_payfitPackage] * numberEmployee;

  return (
    getPayrollProcessEfficiencyGains * 12 +
    getExpenseManagementEfficiencyGains * 12 +
    getTimeSavingsOnPerformanceManagement * 12 +
    getCostsAvoidance * 12 +
    getStaffManagementEfficiencyGains * 12
  );
};

export const getPayrollManagementSavingsPercentage = (
  payfitPackage: string,
): string => {
  if (
    payfitPackage &&
    payfitPackage !== PayfitPackageEnum[PayfitPackageEnum.Paie]
  ) {
    return '90%';
  }

  return '50%';
};

export const getExpenseSavingsPercentage = (
  payfitPackage: string,
  moduleExpense: boolean,
): string => {
  if (
    payfitPackage &&
    payfitPackage !== PayfitPackageEnum[PayfitPackageEnum.Paie] &&
    moduleExpense
  ) {
    return '75%';
  }

  return 'N/A';
};

export const getPerformanceManagement = (
  payfitPackage: string,
  numberEmployee: number,
): string => {
  if (
    numberEmployee &&
    payfitPackage &&
    payfitPackage !== PayfitPackageEnum[PayfitPackageEnum.Paie] &&
    payfitPackage !== PayfitPackageEnum[PayfitPackageEnum['Paie Avancée']]
  ) {
    return `${Math.ceil((2.4 * numberEmployee) / 8)} jours / an`;
  }

  return 'N/A';
};

export const getTaskSavings = (
  payfitPackage: string,
  numberEmployee: number,
): string => {
  if (
    numberEmployee &&
    payfitPackage &&
    payfitPackage !== PayfitPackageEnum[PayfitPackageEnum.Paie]
  ) {
    return `1,5 h / tâche`;
  }

  return 'N/A';
};

export const getCumulativePayfitCosts = (
  payfitPackage: string,
  numberEmployee: number,
  moduleExpense: boolean,
): number => {
  const _payfitPackage = getIndexPayfitPackageEnum(payfitPackage);
  const year1 = getYearCostsResults(
    _payfitPackage,
    numberEmployee,
    moduleExpense,
    true,
  );
  const year2 = getYearCostsResults(
    _payfitPackage,
    numberEmployee,
    moduleExpense,
    false,
  );
  const year3 = getYearCostsResults(
    _payfitPackage,
    numberEmployee,
    moduleExpense,
    false,
  );

  return Number.isNaN(year1 + year2 + year3) ? 0 : year1 + year2 + year3;
};

export const getCumulativePayfitGains = (
  payfitPackage: string,
  numberEmployee: number,
  moduleExpense: boolean,
): number => {
  const year1 = getYearGainsResults(
    payfitPackage,
    numberEmployee,
    moduleExpense,
  );
  const year2 = getYearGainsResults(
    payfitPackage,
    numberEmployee,
    moduleExpense,
  );
  const year3 = getYearGainsResults(
    payfitPackage,
    numberEmployee,
    moduleExpense,
  );

  return Number.isNaN(year1 + year2 + year3) ? 0 : year1 + year2 + year3;
};

export const getROI = (costs: number, gains: number): number => {
  if (costs === 0 || gains === 0) {
    return 0;
  }

  return Math.ceil((gains / costs - 1) * 100);
};

export const getYearGainsDetailedResults = (
  payfitPackage: string,
  numberEmployee: number,
  moduleExpense: boolean,
) => {
  const _payfitPackage = getIndexPayfitPackageEnum(payfitPackage);
  const getPayrollProcessEfficiencyGains =
    payrollProcessEfficiencyGains[_payfitPackage] * numberEmployee;

  const getExpenseManagementEfficiencyGains = moduleExpense
    ? expenseManagementEfficiencyGain[_payfitPackage] * numberEmployee
    : 0;

  const getTimeSavingsOnPerformanceManagement =
    timeSavingsOnPerformanceManagement[_payfitPackage] * numberEmployee;

  const getCostsAvoidance = costAvoidance[_payfitPackage] * numberEmployee;

  const getStaffManagementEfficiencyGains =
    staffManagementEfficiencyGains[_payfitPackage] * numberEmployee;

  return {
    payrollProcessEfficiencyGains: getPayrollProcessEfficiencyGains
      ? getPayrollProcessEfficiencyGains * 12 * 3
      : 0,
    expenseManagementEfficiencyGain: getExpenseManagementEfficiencyGains
      ? getExpenseManagementEfficiencyGains * 12 * 3
      : 0,
    timeSavingsOnPerformanceManagement: getTimeSavingsOnPerformanceManagement
      ? getTimeSavingsOnPerformanceManagement * 12 * 3
      : 0,
    costAvoidance: getCostsAvoidance ? getCostsAvoidance * 12 * 3 : 0,
    staffManagementEfficiencyGains: getStaffManagementEfficiencyGains
      ? getStaffManagementEfficiencyGains * 12 * 3
      : 0,
  };
};

function getEmployeeRangeByNbEmployees(
  numberEmployee: number,
  payfitPackage: string | number,
): NumberEmployeeRangeEnum | undefined {
  if (numberEmployee > 0 && numberEmployee < 4) {
    return NumberEmployeeRangeEnum['1-3'];
  }
  if (numberEmployee > 3 && numberEmployee < 10) {
    return NumberEmployeeRangeEnum['4-9'];
  }
  if (numberEmployee > 9 && numberEmployee < 25) {
    return NumberEmployeeRangeEnum['10-24'];
  }
  if (
    numberEmployee > 24 &&
    payfitPackage !== PayfitPackageEnum[PayfitPackageEnum.Premium]
  ) {
    return NumberEmployeeRangeEnum['25+'];
  }
  if (
    numberEmployee > 24 &&
    numberEmployee < 50 &&
    payfitPackage === PayfitPackageEnum[PayfitPackageEnum.Premium]
  ) {
    return NumberEmployeeRangeEnum['25-49'];
  }
  if (
    numberEmployee > 49 &&
    numberEmployee < 165 &&
    payfitPackage === PayfitPackageEnum[PayfitPackageEnum.Premium]
  ) {
    return NumberEmployeeRangeEnum['50-149'];
  }

  return undefined;
}

function getPayFitPackage(
  payfitPackage: string | number,
  numberEmployeeRange: NumberEmployeeRangeEnum,
): PayFitPackages | undefined {
  return payfitPackages
    .filter((p) => p.payfitPackage === payfitPackage)
    .find((p) => p.numberEmployeeRange === numberEmployeeRange);
}
